import { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Download, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

import { useUserRole } from "../../hooks/useUserRole";

export default function AppointmentNoteActions({ appointmentId, notes = [], hasClinicalContent = false, token }) {
  const { isReceptionistOnly } = useUserRole();
  const [isExporting, setIsExporting] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [includeMedia, setIncludeMedia] = useState(false);
  const hasContent = hasClinicalContent || (Array.isArray(notes) && notes.some(note => note.type === "text"));
  if (isReceptionistOnly || !hasContent) return null;

  const exportPdf = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/appointments/${appointmentId}/notes/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `notes_${appointmentId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const emailNotes = async () => {
    setIsEmailing(true);
    try {
      await axios.post(`${API}/appointments/${appointmentId}/notes/email`,
        { include_media: includeMedia },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Notes emailed to patient!");
      setShowEmailDialog(false);
      setIncludeMedia(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to email notes");
    } finally {
      setIsEmailing(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={exportPdf} disabled={isExporting}>
          {isExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export PDF
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setShowEmailDialog(true)} disabled={isEmailing}>
          {isEmailing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email to Patient
        </Button>
      </div>
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email Notes to Patient</DialogTitle>
            <DialogDescription>Send this appointment&apos;s clinical notes PDF to the patient.</DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 py-4 text-sm text-slate-600">
            <input type="checkbox" checked={includeMedia} onChange={event => setIncludeMedia(event.target.checked)} />
            Include media files (images/PDFs as links, videos as 7-day links)
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowEmailDialog(false)} disabled={isEmailing}>Cancel</Button>
            <Button type="button" onClick={emailNotes} disabled={isEmailing}>
              {isEmailing && <Loader2 size={14} className="animate-spin" />} Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

AppointmentNoteActions.propTypes = {
  appointmentId: PropTypes.string.isRequired,
  notes: PropTypes.arrayOf(PropTypes.object),
  hasClinicalContent: PropTypes.bool,
  token: PropTypes.string,
};
